import { Button } from '@exam-prep/ui';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type SyntheticEvent,
} from 'react';
import { gateSubmit, isFormValid } from '../lib/validation/submit-gate';

const FormValid = createContext(true);

/**
 * A form that never submits invalid input: on submit every invalid field shows its inline
 * message and the first one is focused; `onValidSubmit` runs only when all fields pass.
 */
export function ValidatedForm({
  onValidSubmit,
  children,
  ...props
}: Omit<ComponentProps<'form'>, 'onSubmit' | 'noValidate'> & { onValidSubmit: () => void }) {
  const ref = useRef<HTMLFormElement>(null);
  // Invalid until the first check, so a gated button never starts enabled.
  const [valid, setValid] = useState(false);
  const recheck = () => {
    if (ref.current) setValid(isFormValid(ref.current));
  };
  // Fields set their validity in layout effects, so it is current by the time this runs.
  useEffect(recheck);
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    gateSubmit(event.currentTarget, onValidSubmit);
  };
  return (
    <form {...props} ref={ref} noValidate onSubmit={submit} onInput={recheck} onChange={recheck}>
      <FormValid.Provider value={valid}>{children}</FormValid.Provider>
    </form>
  );
}

/**
 * The submit button of a {@link ValidatedForm}. With `disableUntilValid` it stays disabled until
 * every field passes (for short forms like the 2FA code); otherwise a click shows what is wrong.
 */
export function SubmitButton({
  disableUntilValid = false,
  disabled,
  ...props
}: Omit<ComponentProps<typeof Button>, 'type'> & { disableUntilValid?: boolean }) {
  const valid = useContext(FormValid);
  return <Button {...props} type="submit" disabled={disabled || (disableUntilValid && !valid)} />;
}
